import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import postMessageToParent from '../../helpers/postMessageToParent';
import useParentWindowCoordination from '../../hooks/useParentWindowCoordination';

import OverlayStatusContext from './OverlayStatusContext';
import OverlayStatusData from './types/OverlayStatusData';

type OverlayStatusProviderProps = {
    children: ReactNode;
};

const OverlayStatusProvider = ({ children }: OverlayStatusProviderProps) => {
    const [isClosing, setIsClosing] = useState<boolean>(false);
    const [forcedOverlay, setForcedOverlay] = useState<boolean>(false);
    const [parentSupportsRedirects, setParentSupportsRedirects] =
        useState<boolean>(false);

    const closeIframeOverlay = useCallback(() => {
        setIsClosing(true);
    }, []);

    const forceOverlay = useCallback(() => {
        setForcedOverlay(true);
    }, []);

    const enableParentRedirects = useCallback(() => {
        setParentSupportsRedirects(true);
    }, []);

    useEffect(() => {
        postMessageToParent({ type: 'lb-load' });
    }, []);

    const openerUrl = useParentWindowCoordination();

    useEffect(() => {
        if (!isClosing) {
            return;
        }

        postMessageToParent({
            type: 'lb-close',
        });
    }, [isClosing]);

    const value = useMemo<OverlayStatusData>(
        () => ({
            overlayIsClosing: isClosing,
            closeOverlay: closeIframeOverlay,
            forceOverlay: forceOverlay,
            enableParentRedirects: enableParentRedirects,
            inOverlay: window.top !== window || forcedOverlay || false,
            openerUrl: openerUrl,
            parentSupportsRedirects: parentSupportsRedirects,
        }),
        [
            isClosing,
            closeIframeOverlay,
            forcedOverlay,
            forceOverlay,
            enableParentRedirects,
            openerUrl,
            parentSupportsRedirects,
        ]
    );

    return (
        <OverlayStatusContext.Provider value={value}>
            {children}
        </OverlayStatusContext.Provider>
    );
};

export default OverlayStatusProvider;
